import React from 'react';
import styled, { keyframes } from 'styled-components';
import { GiCartwheel } from "react-icons/gi";
import { IoMdTime } from "react-icons/io";
import { FaRegStopCircle } from "react-icons/fa";
import { TbCircuitSwitchOpen } from "react-icons/tb";
import ProgressBar from '../utils/ProgressBar';
import { generateRandomNumber } from '../utils/RandomNumberGenerator';

const PartArticle = styled.div`
  font-weight: 500;
`;

const PartDescription = styled.div`
  opacity: 0.8;
`;
// Animation (kept close to your original, but fixed to keep translate + rotate together)
const rotate = keyframes`
  to {
    transform: translate(-50%, -50%) rotate(360deg);
  }
`;

const Card = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 1rem;

  padding: 1rem;
  width: 34rem;
  background-color: var(--color-grey-50);
  border-radius: 1rem;
  box-shadow: 0px -16px 24px 0px rgba(255, 255, 255, 0.25) inset;
`;

const CardBorder = styled.div`
  overflow: hidden;
  pointer-events: none;

  position: absolute;
  z-index: -10;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);

  width: calc(100% + 2px);
  height: calc(100% + 2px);
  background-image: linear-gradient(
    0deg,
    var(--color-grey-800) -50%,
    var(--color-witron-blue) 100%
  );

  border-radius: 1rem;

  &::before {
    content: "";
    pointer-events: none;

    position: fixed;
    z-index: 200;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%) rotate(0deg);
    transform-origin: left;

    width: 200%;
    height: 10rem;
    background-image: linear-gradient(
      0deg,
      hsla(0, 0%, 100%, 0) 50%,
      var(--color-brand-900) 10%,
      var(--color-brand-900) 10%,
      hsla(0, 0%, 40%, 0) 100%
    );

    animation: ${rotate} 8s linear infinite;
  }
`;

const CardTitleContainer = styled.div``;

const CardTitle = styled.span`
  font-size: 0.9em;
  color: var(--white);
`;

const CardArticle = styled.p`
  margin-top: 0.05rem;
  width: 65%;
  font-size: 0.7em;
  color: var(--paragraph);
`;

const Line = styled.hr`
  width: 100%;
  height: 0.1rem;
  background-color: var(--line);
  border: none;
`;

const CardList = styled.ul`
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
`;


const CardListItem = styled.li`
  display: grid;
  grid-template-columns: 1.2fr 1.2fr 1fr;
  align-items: center;  
`;


const LeftInfo = styled.div`
  display: flex;
  align-items: center;  
  color: var(--color-red-100);
`;

const CenterInfo = styled.div`
  display: flex;
  align-items: center;  
`;



const ListText = styled.span`
  font-size: 0.6em;
  color: var(--white);
`;


const ListProgressbar = styled.div`
  display: flex;
  width: 100%;
  margin-left: auto; 
`;


const Button = styled.button`
  cursor: pointer;
  align-self: end;
  padding: 0.3rem;
  width: 40%;
  background-image: linear-gradient(
    0deg,
    #000000,
    var(--color-witron-blue) 100%
  );

  font-size: 0.75rem;
  color: var(--color-grey-900);

  border: 0;
  border-radius: 5px;
  box-shadow: inset 0 -2px 25px -4px var(--white);
`;


function MachineDisplayPart({ part }) {

  

  const getPartMaxProgress = (part) => {
    const latestCounter = part.usageCounters
      ?.slice()
      .sort((a, b) => new Date(b.timestampUtc) - new Date(a.timestampUtc))[0];

    if (!latestCounter) return 0;

    const percentages = [
      formatProgressbar(
        latestCounter.operatingHours,
        part.endOfLife?.operatingHours
      ),
      formatProgressbar(latestCounter.distance, part.endOfLife?.distance),
      formatProgressbar(
        latestCounter.brakeApplication,
        part.endOfLife?.brakeApplication
      ),
      formatProgressbar(
        latestCounter.switchingCycles,
        part.endOfLife?.switchingCycles
      ),
    ].filter(Boolean);

    return percentages.length ? Math.max(...percentages) : 0;
  };

  const latestCounter = part.usageCounters
    ?.slice()
    .sort((a, b) => new Date(b.timestampUtc) - new Date(a.timestampUtc))[0];

const formatOperatingHours = (hours) => {
  if (hours == null) return "—";
  return `${(Math.round(hours * 10) / 10).toLocaleString()} h`;
};

const formatSwitchingCycles = (cycles) => {
  if (cycles == null || cycles === 0) return "—";
  return Math.round(cycles).toLocaleString();
};

const formatBrakeApplications = (count) => {
  if (count == null) return "—";
  return Math.round(count).toLocaleString();
};

const formatDistance = (km) => {
  if (km == null) return "—";
  return `${Math.round(km).toLocaleString()} km`;
};

const formatProgressbar = (value, base) => {
  if (value == null || value === 0) return;
  if (base == null || base === 0) return;
  return Math.max(1, Math.round((value * 100) / base));
}

console.log("Latest Counters", latestCounter)


  return (
    <Card>
      <CardBorder />

      <CardTitleContainer>
        <CardTitle>{part.description}</CardTitle>
        <CardArticle>{part.articleNumber}</CardArticle>
      </CardTitleContainer>

      <Line />

      <CardList>
        <CardListItem>
          <LeftInfo>
            <IoMdTime />
            <ListText>
              {formatOperatingHours(part.endOfLife?.operatingHours)}
            </ListText>
          </LeftInfo>
          <CenterInfo>
            <ListText>
              {formatOperatingHours(latestCounter?.operatingHours)}
            </ListText>
          </CenterInfo>
          {formatProgressbar(
            latestCounter?.operatingHours,
            part.endOfLife?.operatingHours
          ) && (
            <ListProgressbar>
              <ProgressBar
                progress={formatProgressbar(
                  latestCounter?.operatingHours,
                  part.endOfLife?.operatingHours
                )}
              />
            </ListProgressbar>
          )}
        </CardListItem>
        <CardListItem>
          <LeftInfo>
            <GiCartwheel />
            <ListText>{formatDistance(part.endOfLife?.distance)}</ListText>
          </LeftInfo>
          <CenterInfo>
            <ListText>{formatDistance(latestCounter?.distance)}</ListText>
          </CenterInfo>
          {formatProgressbar(
            latestCounter?.distance,
            part.endOfLife?.distance
          ) && (
            <ListProgressbar>
              <ProgressBar
                progress={formatProgressbar(
                  latestCounter?.distance,
                  part.endOfLife?.distance
                )}
              />
            </ListProgressbar>
          )}
        </CardListItem>
        <CardListItem>
          <LeftInfo>
            <FaRegStopCircle />
            <ListText>
              {formatBrakeApplications(part.endOfLife?.brakeApplication)}
            </ListText>
          </LeftInfo>
          <CenterInfo>
            <ListText>
              {formatBrakeApplications(latestCounter?.brakeApplication)}
            </ListText>
          </CenterInfo>
          {formatProgressbar(
            latestCounter?.brakeApplication,
            part.endOfLife?.brakeApplication
          ) && (
            <ListProgressbar>
              <ProgressBar
                progress={formatProgressbar(
                  latestCounter?.brakeApplication,
                  part.endOfLife?.brakeApplication
                )}
              />
            </ListProgressbar>
          )}
        </CardListItem>

        <CardListItem>
          <LeftInfo>
            <TbCircuitSwitchOpen />
            <ListText>
              {formatSwitchingCycles(part.endOfLife?.switchingCycles)}
            </ListText>
          </LeftInfo>
          <CenterInfo>
            <ListText>
              {formatSwitchingCycles(latestCounter?.switchingCycles)}
            </ListText>
          </CenterInfo>
          {formatProgressbar(
            latestCounter?.switchingCycles,
            part.endOfLife?.switchingCycles
          ) && (
            <ListProgressbar>
              <ProgressBar
                progress={formatProgressbar(
                  latestCounter?.switchingCycles,
                  part.endOfLife?.switchingCycles
                )}
              />
            </ListProgressbar>
          )}
        </CardListItem>
      </CardList>

      <Button>Click for more ...</Button>
    </Card>
  );
}


export default MachineDisplayPart;